// Project detail (sprint 2/3; sprint 7 §7.8 original layout; sprint 11 §9.4; sprint 12 §9.2): back link, header
// card with the project colour border and stats, then the tabs Overview · Members · Notes · Settings. Admins get
// all four; a Supervisor gets Members (viewTeam or manageMembers), Notes (any note capability) and Settings
// (changeSettings) from GET /api/oversight/projects; a Member sees the personal view only.
import { todayIn } from '@shared/calendar';
import { allProjectCaps } from '@shared/settings-catalogue';
import { ChevronLeft, Plus, UserPlus } from 'lucide-react';
import { lazy, Suspense, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ColorSwatch, projectBorderStyle } from '@/components/ColorSwatch';
import { EmptyState } from '@/components/EmptyState';
import { EntryFormDialog } from '@/components/entries/EntryFormDialog';
import { Page } from '@/components/Page';
import { SectionTitle } from '@/components/SectionTitle';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMe } from '@/hooks/use-me';
import { NoteDialog } from '@/components/notes/NoteDialog';
import { NotesPanel } from '@/components/notes/NotesPanel';
import { useOversightProjects } from '@/hooks/use-oversight';
import { CHECKIN_PROBLEM_LABEL, CountPill, NOTES_TO_REVIEW_LABEL } from '@/components/alarms/CountPill';
import { useCheckinAlertCounts } from '@/hooks/use-checkin-alerts';
import { useJoinProject } from '@/hooks/use-project-members';
import { useAdminProject, useAdminProjectEntries, useProject, useProjectEntries } from '@/hooks/use-projects';
import { formatDurationHm } from '@/lib/format';
import { NotFoundPage } from '@/pages/NotFoundPage';
import type { Project } from '@/types/projects';
import type { OversightProject, ProjectMember } from '@/types/settings';
import { ProjectEntriesTable } from './ProjectEntriesTable';
import { ProjectMembersTab } from './ProjectMembersTab';
import { ProjectSettingsTab } from './ProjectSettingsTab';

/** Sprint 14 (§8.5): the project's open check-in problems on its Attendance tab. */
function AttendanceTabBadge({ projectId }: { projectId: string }): JSX.Element | null {
  const { byProject } = useCheckinAlertCounts();
  return (
    <span className="ml-2 inline-flex">
      <CountPill count={byProject.get(projectId) ?? 0} label={CHECKIN_PROBLEM_LABEL} />
    </span>
  );
}

// Sprint 13: the Attendance tab loads on demand so it stays out of the entry chunk (sprint 19: no Roster tab).
const AttendanceView = lazy(() => import('@/components/shifts/AttendanceView').then((m) => ({ default: m.AttendanceView })));

function DetailSkeleton(): JSX.Element {
  return (
    <Page width="4xl">
      <Skeleton className="h-48 w-full" />
    </Page>
  );
}

function BackLink(): JSX.Element {
  return (
    <Button asChild variant="ghost" size="sm" className="-ml-2 mb-4">
      <Link to="/projects">
        <ChevronLeft /> Back to projects
      </Link>
    </Button>
  );
}

function Stat({ value, label }: { value: string; label: string }): JSX.Element {
  return (
    <div>
      <p className="font-mono text-lg tabular-nums text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

interface HeaderCardProps {
  project: Project;
  totalLabel: string;
  seconds: number;
  entryCount: number;
  actions?: ReactNode;
}

function HeaderCard({ project, totalLabel, seconds, entryCount, actions }: HeaderCardProps): JSX.Element {
  return (
    <div className="card mb-6" style={projectBorderStyle(project.color)}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <ColorSwatch hex={project.color} size={12} />
          <h1 className="truncate text-xl font-normal text-foreground">{project.name}</h1>
          {project.status === 'archived' && <Badge>Archived</Badge>}
        </div>
        {actions}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-6">
        <Stat value={formatDurationHm(seconds)} label={totalLabel} />
        <Stat value={String(entryCount)} label={entryCount === 1 ? 'entry' : 'entries'} />
      </div>
    </div>
  );
}

/** Add log (sprint 3 §9.7 "Log time"): own entries only, Admins included, and only where the caller may log. */
function useAddLog(project: Project, timezone: string, loggable: boolean): { button: ReactNode; dialog: ReactNode } {
  const [logging, setLogging] = useState(false);
  if (project.status !== 'active' || !loggable) return { button: null, dialog: null };
  return {
    button: (
      <Button variant="outline" size="xs" onClick={() => setLogging(true)}>
        <Plus /> Add log
      </Button>
    ),
    dialog: <EntryFormDialog open={logging} onOpenChange={setLogging} mode="create" initialDate={todayIn(timezone)} contextProjectId={project.id} />,
  };
}

/** Which tabs a viewer gets (sprint 12 §9.2). Admins hold every capability. */
export function projectTabsFor(caps: OversightProject['caps'] | null, isAdmin: boolean): { members: boolean; notes: boolean; settings: boolean } {
  if (isAdmin) return { members: true, notes: true, settings: true };
  if (!caps) return { members: false, notes: false, settings: false };
  return {
    members: caps.viewTeam || caps.manageMembers,
    notes: caps.addNotes || caps.readNotes,
    settings: caps.changeSettings,
  };
}

/**
 * Sprint 13 (§15): the Attendance tab on SHIFT projects. Admins always; a Supervisor with viewAttendance (the
 * capability already requires shift mode). Sprint 19: the Roster tab is gone.
 */
export function shiftTabsFor(caps: OversightProject['caps'] | null, isAdmin: boolean, shiftEnabled: boolean): { attendance: boolean } {
  if (!shiftEnabled) return { attendance: false };
  if (isAdmin) return { attendance: true };
  return { attendance: caps?.viewAttendance === true };
}

function TabFallback(): JSX.Element {
  return <Skeleton className="h-72 w-full rounded-xl" />;
}

interface ProjectTabsProps {
  project: Project;
  overview: ReactNode;
  /** This project's entry in GET /api/oversight/projects (null while loading or for a plain Member). */
  oversight: OversightProject | null;
  isAdmin: boolean;
  viewerId: string;
  timezone: string;
}

function ProjectTabs({ project, overview, oversight, isAdmin, viewerId, timezone }: ProjectTabsProps): JSX.Element {
  const [noteFor, setNoteFor] = useState<ProjectMember | null>(null);
  const tabs = projectTabsFor(oversight?.caps ?? null, isAdmin);
  const shiftTabs = shiftTabsFor(oversight?.caps ?? null, isAdmin, oversight?.shiftEnabled === true);
  if (!tabs.members && !tabs.notes && !tabs.settings && !shiftTabs.attendance) return <>{overview}</>;
  // Admins always pass the note routes; until the oversight list loads, assume notes are on for them.
  const notesProject = oversight ?? (isAdmin ? { ...project, caps: allProjectCaps(), notesEnabled: true } : null);
  const canAddNotes = (oversight?.caps.addNotes ?? isAdmin) && (!isAdmin || (oversight?.notesEnabled ?? true));
  return (
    <>
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {tabs.members && <TabsTrigger value="members">Members</TabsTrigger>}
          {shiftTabs.attendance && (
            <TabsTrigger value="attendance">
              Attendance
              <AttendanceTabBadge projectId={project.id} />
            </TabsTrigger>
          )}
          {tabs.notes && (
            <TabsTrigger value="notes">
              Notes
              <CountPill count={oversight?.pendingReviewCount ?? 0} label={NOTES_TO_REVIEW_LABEL} tone="neutral" />
            </TabsTrigger>
          )}
          {tabs.settings && <TabsTrigger value="settings">Settings</TabsTrigger>}
        </TabsList>
        <TabsContent value="overview">{overview}</TabsContent>
        {tabs.members && (
          <TabsContent value="members">
            <ProjectMembersTab projectId={project.id} viewerId={viewerId} isAdmin={isAdmin} onAddNote={canAddNotes ? setNoteFor : undefined} />
          </TabsContent>
        )}
        {shiftTabs.attendance && (
          <TabsContent value="attendance">
            <Suspense fallback={<TabFallback />}>
              <AttendanceView projectId={project.id} tz={timezone} />
            </Suspense>
          </TabsContent>
        )}
        {tabs.notes && notesProject && (
          <TabsContent value="notes">
            <NotesPanel project={notesProject} timezone={timezone} />
          </TabsContent>
        )}
        {tabs.settings && (
          <TabsContent value="settings">
            <ProjectSettingsTab projectId={project.id} />
          </TabsContent>
        )}
      </Tabs>
      <NoteDialog
        projectId={project.id}
        open={noteFor !== null}
        onOpenChange={(o) => !o && setNoteFor(null)}
        timezone={timezone}
        subjects={[]}
        lockedSubject={noteFor ? { id: noteFor.user.id, name: noteFor.user.name } : null}
        canLinkShift={isAdmin || oversight?.caps.viewAttendance === true}
      />
    </>
  );
}

function UserProjectDetail({ id, timezone, viewerId }: { id: string; timezone: string; viewerId: string }): JSX.Element {
  const detail = useProject(id, true);
  const entries = useProjectEntries(id, detail.isSuccess);
  const oversight = useOversightProjects();
  if (detail.isLoading) return <DetailSkeleton />;
  // Archived, missing and not-loggable projects are indistinguishable (owner decision D.4; sprint 11 §6.4).
  if (detail.isError || !detail.data) return <NotFoundPage />;
  const mine = oversight.data?.find((p) => p.id === id) ?? null;
  return <UserProjectBody project={detail.data.project} timezone={timezone} totals={detail.data.totals} entries={entries} oversight={mine} viewerId={viewerId} />;
}

interface UserBodyProps {
  project: Project;
  timezone: string;
  totals: { ownSeconds: number; entryCount: number };
  entries: ReturnType<typeof useProjectEntries>;
  oversight: OversightProject | null;
  viewerId: string;
}

function UserProjectBody({ project, timezone, totals, entries, oversight, viewerId }: UserBodyProps): JSX.Element {
  const addLog = useAddLog(project, timezone, true);
  const timeLog = (
    <>
      <SectionTitle title="Time log" actions={addLog.button} />
      <ProjectEntriesTable
        entries={entries.data?.pages.flatMap((p) => p.items) ?? []}
        showPerson={false}
        loading={entries.isLoading}
        hasMore={entries.hasNextPage}
        loadingMore={entries.isFetchingNextPage}
        onLoadMore={() => void entries.fetchNextPage()}
      />
    </>
  );
  return (
    <Page width="4xl">
      <BackLink />
      <HeaderCard project={project} totalLabel="your total" seconds={totals.ownSeconds} entryCount={totals.entryCount} />
      <ProjectTabs project={project} overview={timeLog} oversight={oversight} isAdmin={false} viewerId={viewerId} timezone={timezone} />
      {addLog.dialog}
    </Page>
  );
}

function AdminProjectDetail({ id, timezone, viewerId }: { id: string; timezone: string; viewerId: string }): JSX.Element {
  const detail = useAdminProject(id, true);
  const [person, setPerson] = useState('');
  const entries = useAdminProjectEntries(id, person || undefined, detail.isSuccess);
  if (detail.isLoading) return <DetailSkeleton />;
  if (detail.isError || !detail.data) return <NotFoundPage />;
  return <AdminProjectBody detail={detail.data} timezone={timezone} person={person} setPerson={setPerson} entries={entries} viewerId={viewerId} />;
}

interface AdminBodyProps {
  detail: NonNullable<ReturnType<typeof useAdminProject>['data']>;
  timezone: string;
  person: string;
  setPerson: (person: string) => void;
  entries: ReturnType<typeof useAdminProjectEntries>;
  viewerId: string;
}

function AdminProjectBody({ detail, timezone, person, setPerson, entries, viewerId }: AdminBodyProps): JSX.Element {
  const { project, perUser } = detail;
  const oversight = useOversightProjects();
  const mine = oversight.data?.find((p) => p.id === project.id) ?? null;
  const myRole = project.config?.myRole ?? null;
  const addLog = useAddLog(project, timezone, myRole !== null);
  const join = useJoinProject(project.id);
  const joinButton =
    myRole === null ? (
      <Button variant="outline" size="sm" disabled={join.isPending} onClick={() => join.mutate(undefined, { onSuccess: () => toast.success(`You joined ${project.name}.`) })}>
        <UserPlus aria-hidden="true" />
        {join.isPending ? 'Joining…' : 'Join as Member'}
      </Button>
    ) : null;

  return (
    <Page width="4xl">
      <BackLink />
      <HeaderCard project={project} totalLabel="team total" seconds={project.teamSeconds} entryCount={project.entryCount} actions={joinButton} />
      <ProjectTabs
        project={project}
        oversight={mine}
        isAdmin
        viewerId={viewerId}
        timezone={timezone}
        overview={
          <>
          <section className="mb-8">
            <SectionTitle title="By person" />
            {perUser.length === 0 ? (
              <EmptyState title="No time logged for this project yet." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Person</TableHead>
                    <TableHead className="text-right">Time</TableHead>
                    <TableHead className="text-right">Entries</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {perUser.map((row) => (
                    <TableRow key={row.user.id}>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5">
                          {row.user.name}
                          {row.user.status === 'deactivated' && <Badge>Deactivated</Badge>}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{formatDurationHm(row.seconds)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{row.entryCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </section>

          <section>
            <SectionTitle
              title="All entries"
              actions={
                <>
                  {addLog.button}
                  {perUser.length > 0 && (
                    <>
                      <Label className="sr-only" htmlFor="person-filter">
                        Person
                      </Label>
                      <NativeSelect id="person-filter" className="w-44" value={person} onChange={(e) => setPerson(e.target.value)}>
                        <option value="">Everyone</option>
                        {perUser.map((row) => (
                          <option key={row.user.id} value={row.user.id}>
                            {row.user.name}
                          </option>
                        ))}
                      </NativeSelect>
                    </>
                  )}
                </>
              }
            />
            <ProjectEntriesTable
              entries={entries.data?.pages.flatMap((p) => p.items) ?? []}
              showPerson
              loading={entries.isLoading}
              hasMore={entries.hasNextPage}
              loadingMore={entries.isFetchingNextPage}
              onLoadMore={() => void entries.fetchNextPage()}
            />
          </section>
          </>
        }
      />
      {addLog.dialog}
    </Page>
  );
}

/** Admins use /api/admin/projects/:id… (any status); everyone else the owner-scoped personal routes. */
export function ProjectDetailPage(): JSX.Element {
  const { projectId = '' } = useParams();
  const { data: me, isLoading } = useMe();
  if (isLoading || !me) return <DetailSkeleton />;
  return me.role === 'admin' ? (
    <AdminProjectDetail id={projectId} timezone={me.timezone} viewerId={me.id} />
  ) : (
    <UserProjectDetail id={projectId} timezone={me.timezone} viewerId={me.id} />
  );
}
